/**
 * Progressive enhancement only. The site must remain fully usable with
 * JavaScript disabled — content and navigation are server-rendered.
 */

// Runs immediately (script is deferred, so the DOM already exists) to avoid a
// flash of hidden .reveal content before DOMContentLoaded fires.
document.documentElement.classList.add("js-enabled");

function initRevealOnScroll(): void {
  const targets = document.querySelectorAll<HTMLElement>(".reveal");
  if (!("IntersectionObserver" in window) || targets.length === 0) {
    targets.forEach((el) => el.classList.add("visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.09 }
  );

  targets.forEach((el) => observer.observe(el));
}

document.addEventListener("DOMContentLoaded", initRevealOnScroll);

function initMobileNav(): void {
  const toggle = document.querySelector<HTMLButtonElement>(".nav-toggle");
  const menu = document.getElementById("primary-navigation");
  if (!toggle || !menu) return;

  const close = (): void => {
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Open menu");
    menu.classList.remove("is-open");
  };

  const open = (): void => {
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Close menu");
    menu.classList.add("is-open");
  };

  toggle.addEventListener("click", () => {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    if (isOpen) close();
    else open();
  });

  menu.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", close);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });

  document.addEventListener("click", (event) => {
    const target = event.target as Node;
    if (!menu.contains(target) && !toggle.contains(target)) close();
  });
}

document.addEventListener("DOMContentLoaded", initMobileNav);

function initImageLightbox(): void {
  // Scoped to project detail page classes only — these selectors never
  // appear on other page templates.
  const images = document.querySelectorAll<HTMLImageElement>(
    ".project-detail-hero img, .case-figure img, .project-detail img"
  );
  if (images.length === 0) return;

  let overlay: HTMLDivElement | null = null;
  let lastTrigger: HTMLImageElement | null = null;
  const TRANSITION_MS = 220;

  const close = (): void => {
    if (!overlay) return;
    const el = overlay;
    overlay = null;
    el.classList.remove("is-visible");
    window.setTimeout(() => el.remove(), TRANSITION_MS);
    lastTrigger?.focus();
    lastTrigger = null;
  };

  const open = (img: HTMLImageElement): void => {
    lastTrigger = img;
    overlay = document.createElement("div");
    overlay.className = "image-lightbox";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", img.alt || "Enlarged image");

    const full = document.createElement("img");
    full.src = img.currentSrc || img.src;
    full.alt = img.alt;
    overlay.appendChild(full);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "image-lightbox-close";
    closeButton.setAttribute("aria-label", "Close enlarged image");
    closeButton.textContent = "✕";
    overlay.appendChild(closeButton);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay || event.target === closeButton) close();
    });

    document.body.appendChild(overlay);
    closeButton.focus();
    // Double rAF so the browser paints the initial (hidden) state before the
    // "is-visible" class is added, guaranteeing the CSS transition runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => overlay?.classList.add("is-visible"));
    });
  };

  images.forEach((img) => {
    img.classList.add("is-zoomable");
    img.tabIndex = 0;
    img.setAttribute("role", "button");
    img.setAttribute("aria-label", (img.alt || "Image") + " — view larger");
    img.addEventListener("click", () => open(img));
    img.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open(img);
      }
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });
}

document.addEventListener("DOMContentLoaded", initImageLightbox);
